import { DetailViewModel } from "@frui.ts/datascreens/dist";
import { bound } from "@frui.ts/helpers/dist";
import { watchBusy } from "@frui.ts/screens/dist";
import { attachAutomaticValidator, validate } from "@frui.ts/validation/dist";
import * as Sentry from "@sentry/react";
import { Modules } from "config";
import type ChargeResponse from "entities/chargeResponse";
import type { ChargeVehicleChangeRequest } from "entities/chargeVehicleChangeRequest";
import { ChargeVehicleChangeValidationRules, ChargeVehicleChange } from "entities/chargeVehicleChangeRequest";
import ChargeVehicleRegistrationChangeRejectionReason from "entities/chargeVehicleRegistrationChangeRejectionReason";
import VehicleChangePreviewResponse from "entities/vehicleChangePreviewResponse";
import type VehicleRegisterSynchronizationStatus from "entities/vehicleRegisterSynchronizationStatus";
import type { interfaces } from "inversify";
import { computed, observable } from "mobx";
import ChargesRepository from "repositories/chargesRepository";
import EnumService from "services/enumService";
import EventBus, { licensePlateChangeChanged } from "services/eventBus";
import LocalizationService from "services/localizationService";
import NotificationService, { NotificationType } from "services/notificationService";
import { emptyValue } from "utils/emptyValue";
import CountrySelectViewModel from "viewModels/countrySelectViewModel";

export default class ChangeLicensePlateViewModel extends DetailViewModel<ChargeVehicleChange> {
  countrySelectViewModel: CountrySelectViewModel;
  @observable changeStep = 1;
  @observable changePlatePreview: VehicleChangePreviewResponse;

  constructor(
    private charge: ChargeResponse,
    private repository: ChargesRepository,
    private localizationService: LocalizationService,
    private eventBus: EventBus,
    private enumService: EnumService,
    private notificationService: NotificationService,
    private countrySelectViewModelFactory: ReturnType<typeof CountrySelectViewModel.Factory>
  ) {
    super();
    this.countrySelectViewModel = this.countrySelectViewModelFactory("id");
  }

  @computed
  get name() {
    return this.translate("name");
  }

  @computed
  get chargeResponse() {
    return this.charge;
  }

  @computed
  get chargeResponseVehicle() {
    return this.charge.vehicle;
  }

  @computed
  get changePreview() {
    return this.changePlatePreview;
  }

  @computed
  get warningReasons() {
    return this.changePlatePreview?.warningReasons;
  }

  @computed
  get rejectedReason() {
    return this.changePlatePreview?.rejectionReason;
  }

  @computed
  get isChangeEmailDisabled() {
    return !!this.chargeResponse.emailAddress;
  }

  @computed
  get rejectedReasonText() {
    if (!this.changePlatePreview?.rejectionReason) {
      return undefined;
    }

    const reason = this.changePlatePreview?.rejectionReason;

    if (reason === ChargeVehicleRegistrationChangeRejectionReason.WarningBypassingForbidden) {
      return this.translate("warningBypassingForbidden");
    }
    return undefined;
  }

  protected loadDetail() {
    const entity = new ChargeVehicleChange(this.charge);

    attachAutomaticValidator(entity, ChargeVehicleChangeValidationRules);

    return entity;
  }

  async onDeactivate(isClosing: boolean) {
    this.item = this.loadDetail();
    return super.onDeactivate(isClosing);
  }

  @bound
  @watchBusy
  async onSave() {
    if (!validate(this.item)) {
      return;
    }

    try {
      await this.repository.changePlate(this.item, this.charge.chargeId, this.isChangeEmailDisabled);
      this.eventBus.publish(licensePlateChangeChanged({ entity: this.item }));
      this.notificationService.add({ title: this.translate("changed"), type: NotificationType.SUCCESS });

      this.requestClose();
    } catch (e) {
      Sentry.captureException(e);
    }
  }

  @bound
  @watchBusy
  async onContinue() {
    if (!validate(this.item)) {
      return;
    }

    try {
      this.changePlatePreview = await this.repository.changePlatePreview(
        this.item,
        this.charge.chargeId,
        this.isChangeEmailDisabled
      );
      this.changeStep = 2;
    } catch (e) {
      Sentry.captureException(e);
    }
  }

  @bound
  onClose() {
    this.requestClose();
  }

  @bound
  onBack() {
    this.changeStep = 1;
    this.changePlatePreview = new VehicleChangePreviewResponse();
  }

  formatCountry(countryId: string): string {
    return `${this.translateCountry(countryId)} - ${this.translateCountryName(countryId)}`;
  }

  translateCountry(countryId: string) {
    return this.enumService.value("countries", countryId)?.code ?? emptyValue;
  }

  translateCountryName(countryId: string) {
    return this.enumService.value("countries", countryId)?.name ?? emptyValue;
  }

  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.callCenter, `licensePlateChange.${code}`, size);
  }

  translateGeneral(code: string, size?: number) {
    return this.localizationService.translateGeneral(code, size);
  }

  translateAttribute(attribute: keyof ChargeVehicleChangeRequest, size?: number) {
    return this.localizationService.translateAttribute("licensePlateChange", attribute, size);
  }

  translateVehicleRegisterSynchronizationStatus(status: VehicleRegisterSynchronizationStatus) {
    return this.localizationService.translateEnum("vehicleRegisterSynchronizationStatus", status);
  }

  static Factory({ container }: interfaces.Context) {
    return (charge: ChargeResponse) =>
      new ChangeLicensePlateViewModel(
        charge,
        container.get(ChargesRepository),
        container.get(LocalizationService),
        container.get(EventBus),
        container.get(EnumService),
        container.get(NotificationService),
        container.get(CountrySelectViewModel.Factory)
      );
  }
}
